import {Button} from '@ui/atoms/button';
import {Body1, Body2} from '@ui/atoms/typography';
import {ItemLoader} from '@ui/organisms/item-loader';
import {
  AnonymousPulseSummaryData,
  MIN_ANONYMOUS_PULSE_ANSWERS,
} from './anonymous-pulse-summary.utils';

type AnonymousPulseSummaryProps = {
  summary: AnonymousPulseSummaryData;
  hasSubmittedResponses: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

export const AnonymousPulseSummary = ({
  summary,
  hasSubmittedResponses,
  isLoading = false,
  isError = false,
  onRetry,
}: AnonymousPulseSummaryProps) => (
  <section
    aria-label="Anonymous pulse summary"
    className="flex flex-col gap-5 rounded-[10px] border border-borderLight bg-white p-5 sm:p-6"
    data-testid="anonymous-pulse-summary">
    <div>
      <Body1 kind="blackTwo" weight="bold">
        Anonymous pulse summary
      </Body1>
      <Body2 kind="textBody">
        Only grouped results are shown on this page.
      </Body2>
    </div>

    {isLoading ? (
      <div className="flex min-h-[152px] items-center justify-center">
        <ItemLoader />
      </div>
    ) : isError ? (
      <div className="flex min-h-[152px] flex-col items-center justify-center gap-3 text-center">
        <Body2 kind="textBody">Unable to load anonymous results.</Body2>
        {onRetry && (
          <Button kind="secondary" style={{width: 'auto'}} onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    ) : !hasSubmittedResponses ? (
      <div className="flex min-h-[152px] items-center justify-center text-center">
        <Body2 kind="textBody">No anonymous responses yet.</Body2>
      </div>
    ) : summary.responseCount < MIN_ANONYMOUS_PULSE_ANSWERS ? (
      <div className="flex min-h-[152px] items-center justify-center text-center">
        <Body2 kind="textBody">
          Results appear after at least {MIN_ANONYMOUS_PULSE_ANSWERS} people
          answer the pulse.
        </Body2>
      </div>
    ) : (
      <div className="flex flex-col gap-3">
        {summary.breakdown.map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between gap-4 rounded-lg bg-backgroundDark px-4 py-3">
            <Body2 kind="textBody" weight="bold">
              {item.emoji} {item.label}
            </Body2>
            <Body2 kind="blackTwo" weight="bold">
              {item.percentage}% ({item.count})
            </Body2>
          </div>
        ))}
      </div>
    )}
  </section>
);
